import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { APPLIANCE_TYPES, ORTHO_PHASES, ARCH_OPTIONS, CASE_STATUS, todayIso } from "@/lib/ortho";
import { toast } from "sonner";

const EMPTY = {
  appliance_type: "brackets_metalicos", start_date: todayIso(), estimated_months: 18, phase: "alineacion",
  doctor_id: "", upper_arch: "", lower_arch: "", elastics: "", diagnosis: "", objectives: "", notes: "", status: "activo",
};

export default function OrthoCaseForm({ open, onOpenChange, patientId, caseData, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const isEdit = !!caseData?.id;

  useEffect(() => {
    if (!open) return;
    setForm(caseData ? { ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, caseData[k] ?? EMPTY[k]])) } : EMPTY);
    api.get("/users", { params: { role: "odontologo" } }).then((r) => setDoctors(r.data)).catch(() => {});
  }, [open, caseData]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, estimated_months: Number(form.estimated_months) || 0, doctor_id: form.doctor_id || null };
      const { data } = isEdit ? await api.put(`/ortho/${caseData.id}`, payload) : await api.post(`/patients/${patientId}/ortho`, payload);
      toast.success(isEdit ? "Caso actualizado" : "Caso de ortodoncia iniciado");
      onOpenChange(false);
      onSaved && onSaved(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="ortho-case-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{isEdit ? "Editar caso de ortodoncia" : "Iniciar caso de ortodoncia"}</DialogTitle>
          <DialogDescription>Ficha base del tratamiento. Los arcos y la fase se actualizan automáticamente con cada control.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label-eyebrow block mb-1">Aparatología</label>
              <select className="input-flat" data-testid="ortho-appliance" value={form.appliance_type} onChange={(e) => set("appliance_type", e.target.value)}>
                {APPLIANCE_TYPES.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label-eyebrow block mb-1">Ortodoncista</label>
              <select className="input-flat" data-testid="ortho-doctor" value={form.doctor_id || ""} onChange={(e) => set("doctor_id", e.target.value)}>
                <option value="">—</option>
                {doctors.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label-eyebrow block mb-1">Fecha de inicio</label>
              <input type="date" required className="input-flat" data-testid="ortho-start" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} />
            </div>
            <div>
              <label className="label-eyebrow block mb-1">Duración estimada (meses)</label>
              <input type="number" min="1" max="60" className="input-flat font-mono" data-testid="ortho-months" value={form.estimated_months} onChange={(e) => set("estimated_months", e.target.value)} />
            </div>
            <div>
              <label className="label-eyebrow block mb-1">Fase actual</label>
              <select className="input-flat" data-testid="ortho-phase" value={form.phase} onChange={(e) => set("phase", e.target.value)}>
                {ORTHO_PHASES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </div>
            {isEdit && (
              <div>
                <label className="label-eyebrow block mb-1">Estado del caso</label>
                <select className="input-flat" data-testid="ortho-status" value={form.status} onChange={(e) => set("status", e.target.value)}>
                  {Object.entries(CASE_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="label-eyebrow block mb-1">Arco superior inicial</label>
              <input list="arch-options" className="input-flat font-mono" value={form.upper_arch} onChange={(e) => set("upper_arch", e.target.value)} placeholder="0.014 NiTi" />
            </div>
            <div>
              <label className="label-eyebrow block mb-1">Arco inferior inicial</label>
              <input list="arch-options" className="input-flat font-mono" value={form.lower_arch} onChange={(e) => set("lower_arch", e.target.value)} placeholder="0.014 NiTi" />
            </div>
            <datalist id="arch-options">{ARCH_OPTIONS.map((a) => <option key={a} value={a} />)}</datalist>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Diagnóstico ortodóncico</label>
            <textarea rows={2} className="input-flat h-auto py-2" data-testid="ortho-diagnosis" value={form.diagnosis} onChange={(e) => set("diagnosis", e.target.value)} placeholder="Clase II div 1, apiñamiento moderado superior, mordida profunda…" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Objetivos del tratamiento</label>
            <textarea rows={2} className="input-flat h-auto py-2" value={form.objectives} onChange={(e) => set("objectives", e.target.value)} placeholder="Corregir relación molar, alinear, cerrar diastemas…" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Notas</label>
            <textarea rows={2} className="input-flat h-auto py-2" value={form.notes} onChange={(e) => set("notes", e.target.value)} />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => onOpenChange(false)} className="h-10 px-4 rounded-sm border border-border text-sm hover:bg-muted">Cancelar</button>
            <button type="submit" disabled={loading} className="btn-primary" data-testid="ortho-case-save">{loading ? "Guardando…" : isEdit ? "Guardar cambios" : "Iniciar caso"}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
